import { FilterItem, filtersLibrary } from "@/components/filter/filter.types.ts";
import { HomeWidgetId } from "@/pages/home/widgets/home-widget.registry.tsx";
import {
  ApiEmployeeLifecycleStatus,
  BirthdayDisplayType,
  EmployeeFullNameRenderType,
} from "@/types/employees/employee.types.ts";
import { ApiPlan } from "@/types/subscription.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { t } from "i18next";
import { isEqual } from "lodash";

export enum ApiOrganizationBackgroundApplyTo {
  new_employees = "new_employees",
  without_own_background = "without_own_background",
  all_employees = "all_employees",
}

export enum Languages {
  english = "en",
  russian = "ru",
}

export enum EmployeeDatesFormat {
  yyyy_mm_dd = "yyyy_mm_dd",
  mm_dd_yyyy = "mm_dd_yyyy",
  dd_mm_yyyy = "dd_mm_yyyy",
  dd_mm_yyyy_dot = "dd.mm.yyyy",
}

export type LanguageItem = SelectItem;
export type NameFormatItem = SelectItem;
export type DateFormatItem = SelectItem;
export type BirthdayDisplayFormatItem = SelectItem;

export interface ApiOrganization {
  title: string;
  language?: Languages;
  timezone?: string;
  employee_name_format?: EmployeeFullNameRenderType;
  default_date_format?: EmployeeDatesFormat;
  logo_url?: string;
  background_url?: string;
  theme?: string;
  birthday_display?: BirthdayDisplayType;
  currency: string;
  work_day_start_hour?: number;
  standard_weekly_hours?: number | null;
}

export interface ApiOrganizationGeneralSettings extends ApiOrganization {
  personnel_number_auto_enabled?: boolean;
  personnel_number_next_value?: number | null;
}

export type ApiOrganizationPayload = ApiOrganizationGeneralSettings;

export const LanguageItems: LanguageItem[] = [
  {
    id: Languages.english,
    title: "common:english",
  },
  {
    id: Languages.russian,
    title: "common:russian",
  },
];

export const NameFormats: NameFormatItem[] = [
  {
    id: EmployeeFullNameRenderType.first_last,
    title: "common:first_last",
  },
  {
    id: EmployeeFullNameRenderType.last_first,
    title: "common:last_first",
  },
  {
    id: EmployeeFullNameRenderType.f_last,
    title: "common:f_last",
  },
];

export const DateFormats: DateFormatItem[] = [
  {
    id: EmployeeDatesFormat.dd_mm_yyyy,
    title: "common:date_placeholder_dd_mm_yyyy",
  },
  {
    id: EmployeeDatesFormat.mm_dd_yyyy,
    title: "common:date_placeholder_mm_dd_yyyy",
  },
  {
    id: EmployeeDatesFormat.yyyy_mm_dd,
    title: "common:date_placeholder_yyyy_mm_dd",
  },
  {
    id: EmployeeDatesFormat.dd_mm_yyyy_dot,
    title: "common:date_placeholder_dd_mm_yyyy_dot",
  },
];

export const BirthdayDisplayFormats: BirthdayDisplayFormatItem[] = [
  {
    id: BirthdayDisplayType.full,
    title: "core:birthday_display_full",
  },
  {
    id: BirthdayDisplayType.short,
    title: "core:birthday_display_short",
  },
  {
    id: BirthdayDisplayType.none,
    title: "core:birthday_display_none",
  },
];

export const LanguageOptions = LanguageItems.map((item) => {
  return {
    id: item.id,
    title: t(item.title),
  };
});
export const NameFormatOptions = NameFormats.map((item) => {
  return {
    id: item.id,
    title: t(item.title),
  };
});
export const DateFormatOptions = DateFormats.map((item) => {
  return {
    id: item.id,
    title: t(item.title),
  };
});
export const BirthdayDisplayOptions = BirthdayDisplayFormats.map((item) => {
  return {
    id: item.id,
    title: t(item.title),
  };
});

export interface ApiFeatureSettings {
  plan: ApiPlan;
}

export interface ApiOrganizationSettingsMetricCalculations {
  voluntary_leave_type_ids?: number[];
}

export interface ApiOrganizationSettingsViewFilter {
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
  division_ids?: number[];
  location_ids?: number[];
  employee_lifecycle_statuses?: ApiEmployeeLifecycleStatus[];
  employment_type_ids?: number[];
  employment_contract_ids?: number[];
}

const library = filtersLibrary();

const viewFilterFieldByName: Record<string, keyof ApiOrganizationSettingsViewFilter> = {
  [library.job_profile_ids.name]: "job_profile_ids",
  [library.job_role_ids.name]: "job_role_ids",
  [library.department_ids.name]: "department_ids",
  [library.division_ids.name]: "division_ids",
  [library.location_ids.name]: "location_ids",
  [library.employee_lifecycle_statuses.name]: "employee_lifecycle_statuses",
  [library.employment_type_ids.name]: "employment_type_ids",
  [library.employment_contract_ids.name]: "employment_contract_ids",
};

export function buildViewFilterSearchParams(
  viewFilter: ApiOrganizationSettingsViewFilter,
  filters: FilterItem[],
  currentParams?: URLSearchParams
): URLSearchParams {
  const searchParams = new URLSearchParams(currentParams);

  for (const filter of filters) {
    const paramName = filter.name;
    if (searchParams.has(paramName)) continue;

    const viewFilterField = viewFilterFieldByName[paramName];
    if (!viewFilterField) continue;

    const paramValue = viewFilter[viewFilterField];
    if (!paramValue || (Array.isArray(paramValue) && !paramValue.length)) continue;

    if (isEqual(paramValue.sort(), filter.defaultValue)) continue;

    searchParams.set(paramName, paramValue.join(","));
  }

  return searchParams;
}

export interface ApiOrganizationHomeLayoutWidget {
  id: HomeWidgetId;
  hidden: boolean;
  locked: boolean;
  is_fixed: boolean;
}

export interface ApiOrganizationSettingsHomeLayout {
  widgets: ApiOrganizationHomeLayoutWidget[];
}

export interface ApiOrganizationHomeLayoutWidgetPayload {
  id: HomeWidgetId;
  hidden: boolean;
  locked: boolean;
}

export interface ApiOrganizationSettingsHomeLayoutPayload {
  widgets: ApiOrganizationHomeLayoutWidgetPayload[];
}
